import React, { useEffect, useState } from 'react'
import { api } from '../../../infrastructure/api'
import { ExcelGeneratorContainer } from '../../../infrastructure/excelExport'
import { Claims } from '../../../infrastructure/signIn/models'
import { hasClaim } from '../../../infrastructure/signIn/userContext'
import { t } from '../../../infrastructure/i18nextHelper'
import { ColumnDescriptor, TableItem, TextField } from '../../common/customComponents'
import { MasterDataItem, MasterDataShell, createExcelLines } from './masterDataShell'
import guid from '../../../infrastructure/guid'

type Family = {
    name: string
}

const toTableItem = (family: Family): TableItem<MasterDataItem<Family>> => ({
    ...family,
    id: family.name,
    isModified: false
})

export default function FamilyMasterData() {
    const tBase = 'admin.masterdata.family.'
    const isManager = hasClaim(Claims.MasterdataFamilyManager)
    const excelGenerator = ExcelGeneratorContainer.useContainer()

    const [families, setFamilies] = useState<Family[]>([])
    const [nameFilter, setNameFilter] = useState<string>('')

    const load = async () => setFamilies(await api.get<Family[]>('masterdata/family'))

    useEffect(() => { load() }, [])

    const getItems = () => families
        .filter(x => x.name.toLowerCase().includes(nameFilter.toLowerCase()))
        .map(toTableItem)

    const columns: ColumnDescriptor<TableItem<MasterDataItem<Family>>>[] = [
        {
            name: t(tBase + 'name'),
            value: x => x.name,
            columnFilter: { value: nameFilter, onChange: (name: string) => setNameFilter(name ?? '') }
        }
    ]

    const onSave = async (item: MasterDataItem<Family>) => {
        await api.post('masterdata/family', {
            name: item.name,
            originalName: item.id === guid.empty ? null : item.id
        })
        await load()
        return true
    }

    const onDelete = async (ids: string[]) => {
        if (ids.length === 0) return false
        await api.del('masterdata/family', { names: ids })
        await load()
        return true
    }

    const exportExcel = () => {
        excelGenerator.generate({
            filename: 'Families.xlsx',
            sheets: [{ name: 'Families', lines: createExcelLines(getItems(), columns) }]
        })
    }

    const onImportExcel = (file: Blob) => {
        api.upload('masterdata/family/import', file, 'family', { withReport: 'dialog' }).then(load)
    }

    return (
        <MasterDataShell
            tableId='family-table'
            headerLabel={t(tBase + 'families')}
            itemLabel={t(tBase + 'family')}
            items={getItems()}
            isManager={isManager}
            onNew={() => ({ name: '' })}
            onSave={onSave}
            onDelete={onDelete}
            onExportExcel={exportExcel}
            onImportExcel={onImportExcel}
            columns={columns}>
            {(selectedItem, setSelectedItem) => (
                <TextField label={t(tBase + 'name')}
                    disabled={!isManager}
                    text={selectedItem.name}
                    onChange={event => setSelectedItem({ ...selectedItem, name: event.target.value })} />
            )}
        </MasterDataShell>
    )
}
